/**
 * @date 2025-10-01
 * @description 右键菜单组件
 * @author lv.chen
 */

import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';

import type { IContextMenuProps, IContextMenuPosition } from './types';

import './index.scss';

/**
 * 右键菜单组件
 * 支持为指定的 DOM 元素添加右键菜单功能
 */
const ContextMenu: React.FC<IContextMenuProps> = (props) => {
  const { rootStyle, rootClassName, style, className, children, menuItems, onMenuClick } = props;

  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<IContextMenuPosition>({ x: 0, y: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * 处理右键点击事件
   */
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const { clientX, clientY } = e;

    // 计算菜单位置,避免超出视口
    const calculatePosition = () => {
      const menuWidth = 160; // 最小宽度
      const menuHeight = menuItems.length * 40; // 估算高度
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let x = clientX;
      let y = clientY;

      // 如果菜单会超出右边界,则向左显示
      if (x + menuWidth > viewportWidth) {
        x = viewportWidth - menuWidth - 10;
      }

      // 如果菜单会超出下边界,则向上显示
      if (y + menuHeight > viewportHeight) {
        y = viewportHeight - menuHeight - 10;
      }

      return { x, y };
    };

    const newPosition = calculatePosition();
    setPosition(newPosition);
    setVisible(true);
  };

  /**
   * 处理菜单项点击事件
   */
  const handleMenuItemClick = (key: string, disabled?: boolean) => {
    if (disabled) {
      return;
    }

    onMenuClick(key);
    setVisible(false);
  };

  /**
   * 处理点击外部关闭菜单
   */
  useEffect(() => {
    if (!visible) {
      return;
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible]);

  /**
   * 处理 ESC 键关闭菜单
   */
  useEffect(() => {
    if (!visible) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setVisible(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible]);

  return (
    <>
      <div
        ref={containerRef}
        style={rootStyle}
        className={classnames('dbh-context-menu-root', rootClassName)}
      >
        {React.cloneElement(children, {
          onContextMenu: handleContextMenu,
        })}
      </div>

      {visible && (
        <div
          ref={menuRef}
          style={{
            ...style,
            left: `${position.x}px`,
            top: `${position.y}px`,
          }}
          className={classnames('dbh-context-menu', className)}
        >
          {menuItems?.map((item) => {
            const {
              key: itemKey,
              disabled: itemDisabled,
              divider: itemDivider,
              icon: itemIcon,
              label: itemLabel,
            } = item || {};
            return (
              <React.Fragment key={itemKey}>
                <div
                  className={classnames('dbh-context-menu__item', {
                    'dbh-context-menu__item--disabled': itemDisabled,
                  })}
                  style={{
                    left: `${position?.x}px`,
                    top: `${position?.y}px`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuItemClick(itemKey, itemDisabled);
                  }}
                >
                  {itemIcon && <div className="dbh-context-menu__item-icon">{itemIcon}</div>}
                  <div className="dbh-context-menu__item-label">{itemLabel}</div>
                </div>
                {itemDivider && <div className="dbh-context-menu__divider" />}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </>
  );
};

export default ContextMenu;
